// Mirror of Shared/Calculators/DeckCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "deckLengthFt", kind: "number", default: 12, min: 2, max: 100 },
  { key: "deckWidthFt", kind: "number", default: 12, min: 2, max: 100 },
  { key: "boardWidthIn", kind: "number", default: 5.5, min: 3, max: 8 },
  { key: "boardGapIn", kind: "number", default: 0.125, min: 0, max: 0.5 },
  {
    key: "joists",
    kind: "select",
    defaultChoice: "16",
    choices: [
      { value: "12", set: { joistSpacingIn: 12 } },
      { value: "16", set: { joistSpacingIn: 16 } },
      { value: "24", set: { joistSpacingIn: 24 } },
    ],
  },
  { key: "pricePerBoard", kind: "number", default: 15, min: 0, max: 200 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const lengthFt = F.get(i, "deckLengthFt");
  const widthFt = F.get(i, "deckWidthFt");
  const boardW = F.get(i, "boardWidthIn");
  const gap = F.get(i, "boardGapIn");
  const joistSpacing = F.get(i, "joistSpacingIn");
  const price = F.get(i, "pricePerBoard");

  const widthIn = widthFt * 12;
  const lengthIn = lengthFt * 12;
  const eff = boardW + gap;
  const rows = eff > 0 ? F.ceilUnits(widthIn / eff) : 0;
  const joists = joistSpacing > 0 ? F.ceilUnits(lengthIn / joistSpacing) + 1 : 0;
  const screws = rows * joists * 2;
  const area = lengthFt * widthFt;
  const cost = rows * price;

  return {
    area: F.round(area, 2),
    deckingBoards: rows,
    joists,
    screws,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const boards = F.get(r, "deckingBoards");
  return [
    { label: "Deck area", value: `${F.dec(F.get(r, "area"))} sq ft` },
    { label: "Decking boards", value: `${F.intStr(boards)} ${F.plural(boards, "board", "boards")}`, highlight: true },
    { label: "Joists", value: F.intStr(F.get(r, "joists")) },
    { label: "Screws", value: `~${F.intStr(F.get(r, "screws"))}` },
    { label: "Est. decking cost", value: F.usd(F.get(r, "cost")), sub: "rough — decking boards only, excludes framing, footings & labor" },
  ];
}

export const deck: CalcDef = { slug: "deck-calculator", fields, compute, present };
